// Frontend Feature Gating Utility
// Uses backend config/feature_tiers.yml as single source of truth via Inertia shared data

// Module-level cache — set once by initFeatureTiers() from the top-level page component,
// then read by all feature-gate functions without needing React hooks.
let _featureTiersCache = null

/**
 * Initialize the feature tiers cache. Call this once from the top-level Inertia
 * page component (e.g., Home) which has access to Inertia shared props.
 *
 * @param {object} tiers - Feature tiers from page.props.feature_tiers
 */
export function initFeatureTiers(tiers) {
  if (tiers) {
    _featureTiersCache = tiers
  }
}

// Minimal fallback — only used in tests or if initFeatureTiers was never called.
const FALLBACK_TIERS = {
  free: { name: 'Free', max_seats: null, features: {} },
  pro: { name: 'Pro', max_seats: null, features: {} }
}

/**
 * Get feature tiers configuration.
 * Reads from the module-level cache set by initFeatureTiers().
 *
 * @returns {object} Feature tiers configuration
 */
function getFeatureTiers() {
  if (_featureTiersCache) {
    return _featureTiersCache
  }

  if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'test') {
    console.warn(
      'Feature tiers cache is empty. Ensure initFeatureTiers() is called from the top-level page component.'
    )
  }

  return FALLBACK_TIERS
}

/**
 * Check if a feature is available for the current workspace
 * @param {string} featureName - The feature to check (e.g., 'automated_reports')
 * @param {object} workspace - The workspace object with plan property
 * @returns {boolean} - True if feature is available
 */
export function isFeatureAvailable(featureName, workspace) {
  const FEATURE_TIERS = getFeatureTiers()

  if (!workspace || !workspace.plan) {
    // Default to free plan if workspace or plan is missing
    return FEATURE_TIERS.free?.features?.[featureName] || false
  }

  const plan = workspace.plan.toLowerCase()
  const tier = FEATURE_TIERS[plan] || FEATURE_TIERS.free

  return tier?.features?.[featureName] || false
}

/**
 * Check if a feature is available for the current workspace (alias)
 * @param {object} workspace - The workspace object with plan property
 * @param {string} featureName - The feature to check
 * @returns {boolean} - True if feature is available
 */
export function canAccessFeature(workspace, featureName) {
  return isFeatureAvailable(featureName, workspace)
}

/**
 * Get all available features for a workspace plan
 * @param {object} workspace - The workspace object with plan property
 * @returns {object} - Object with feature names as keys and availability as values
 */
export function getAvailableFeatures(workspace) {
  const FEATURE_TIERS = getFeatureTiers()

  if (!workspace || !workspace.plan) {
    return FEATURE_TIERS.free?.features || {}
  }

  const plan = workspace.plan.toLowerCase()
  const tier = FEATURE_TIERS[plan] || FEATURE_TIERS.free

  return tier?.features || {}
}

/**
 * Check if workspace is on Pro plan or higher
 * @param {object} workspace - The workspace object with plan property
 * @returns {boolean} - True if workspace is on Pro plan
 */
export function isProWorkspace(workspace) {
  if (!workspace || !workspace.plan) {
    return false
  }

  const plan = workspace.plan.toLowerCase()
  return ['pro', 'premium', 'enterprise'].includes(plan)
}

/**
 * Get the required plan for a feature
 * @param {string} featureName - The feature to check
 * @returns {string} - The plan name that unlocks this feature ('free' or 'pro')
 */
export function getRequiredPlan(featureName) {
  const FEATURE_TIERS = getFeatureTiers()

  // Check if feature is available in free plan
  if (FEATURE_TIERS.free?.features?.[featureName]) {
    return 'free'
  }

  // Check if feature is available in pro plan
  if (FEATURE_TIERS.pro?.features?.[featureName]) {
    return 'pro'
  }

  // Unknown feature, assume Pro
  return 'pro'
}

export default {
  isFeatureAvailable,
  canAccessFeature,
  getAvailableFeatures,
  isProWorkspace,
  getRequiredPlan
}
